import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {  FaEllipsisV } from "react-icons/fa";
import api from "../services/api";
import "./EmployeesPage.css";
// add at top with the other imports
import { createPortal } from "react-dom";

function EmpMenuPortal({ anchorEl, open, onClose, children }) {
  // Always call hooks
  React.useEffect(() => {
    if (!open) return;                // guard inside effect
    const close = () => onClose?.();
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, onClose]);

  if (!open || !anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const style = {
    position: "fixed",                // outside any overflow clipping
    top: rect.top,                 // viewport coords (no scrollY needed)
    left: rect.right,
    transform: "translateX(-100%)",   // right-align to the button
    zIndex: 10000,
    minWidth: 200,
  };

  return createPortal(
    <ul className="emp-menu open" style={style} role="menu">
      {children}
    </ul>,
    document.body
  );
}



/* ======================================================
   SVG TAB DEFS + COMPONENT (rendered once per page)
   ====================================================== */
function TabSvgDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <defs>
        <filter id="tabShadow">
          <feComponentTransfer in="SourceGraphic">
            <feFuncR type="discrete" tableValues="0" />
            <feFuncG type="discrete" tableValues="0" />
            <feFuncB type="discrete" tableValues="0" />
          </feComponentTransfer>
          <feGaussianBlur stdDeviation="1" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.2" />
          </feComponentTransfer>
          <feOffset dx="5" dy="1" result="shadow" />
          <feComposite in="SourceGraphic" />
        </filter>

        {/* Inactive gradient (blue-grey like the mock) */}
        <linearGradient id="tab-inactive-bg" x1="0%" y1="0%" x2="0%" y2="65%">
          <stop offset="0%" stopColor="#E5ECF6" />
          <stop offset="100%" stopColor="#D7E0ED" />
        </linearGradient>

        {/* Active gradient (white) */}
        <linearGradient id="tab-active-bg" x1="0%" y1="0%" x2="0%" y2="65%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#FAFBFC" />
        </linearGradient>

        {/* Tab shape with left inward curve + right downward slope */}
        <symbol id="tab-shape" viewBox="0 0 122 40">
          <path d="M116.486,29.036c-23.582-8-14.821-29-42.018-29h-62.4C5.441,0.036,0,5.376,0,12.003v28.033h122v-11H116.486z" />
        </symbol>
      </defs>
    </svg>
  );
}

function SvgTabs({ tabs, activeIndex, onChange }) {
  return (
    <nav className="svg-tabs" role="navigation" aria-label="Employee sections">
      <ul className="svg-tablist">
        {tabs.map((label, i) => {
          const active = i === activeIndex;
          return (
            <li key={label} className={`svg-tab ${active ? "is-active" : ""}`}>
              <button
                type="button"
                className="svg-tab-link"
                onClick={() => onChange(i)}
                aria-selected={active}
                aria-controls={`panel-${i}`}
                role="tab"
              >
                <span className="svg-tab-label">{label}</span>
                <svg className="svg-tab-bg" viewBox="0 0 122 40" aria-hidden="true">
                  <use xlinkHref="#tab-shape" />
                </svg>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ======================================================
   Small presentational helpers
   ====================================================== */
//const Dot = ({ color = "#2ecc71" }) => <span className="pw-dot" style={{ backgroundColor: color }} />;
const EmployeeAvatar = ({ first = "", last = "" }) => {
  const initials = `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();
  return <div className="pw-avatar">{initials || "?"}</div>;
};
const StatCard = ({ icon, label, value }) => (
  <div className="pw-stat">
    <div className="pw-stat-icon">{icon}</div>
    <div className="pw-stat-right">
      <div className="pw-stat-value">{value}</div>
      <div className="pw-stat-label">
        {label.title}
        <div className="pw-stat-sub">{label.sub}</div>
      </div>
    </div>
  </div>
);

/* ======================================================
   PAGE
   ====================================================== */
   const EmployeesPage = ({ selectedBusiness }) => {

  const businessId = selectedBusiness?.id;
  const navigate = useNavigate();

  /** -------- Tabs (string <-> index mapping) -------- */
  const TAB_KEYS = ["details", "status", "department"];
  const TAB_LABELS = ["Active", "Inactive", "Departments"];
  const [activeTab, setActiveTab] = useState("details");
  const activeTabIndex = Math.max(0, TAB_KEYS.indexOf(activeTab));
  const setActiveByIndex = (i) => setActiveTab(TAB_KEYS[i] ?? "details");

  /** -------- Page state -------- */
  // details tab
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [q] = useState("");

  // status tab (inactive employees)
  const [inactive, setInactive] = useState([]);
  const [loadingInactive, setLoadingInactive] = useState(false);
  const [inactiveLoaded, setInactiveLoaded] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  // department tab
  const [departments, setDepartments] = useState([]);
  const [deptLoaded, setDeptLoaded] = useState(false);
  const [loadingDept, setLoadingDept] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [newDepartmentShort, setNewDepartmentShort] = useState("");
  const [deptError, setDeptError] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);


  /** -------- Fetchers -------- */
  // employees
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/businesses/${businessId}/lite-employees`);
        if (!alive) return;
        setEmployees(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error fetching employees:", e);
        alert("Failed to load employees.");
      } finally {
        alive && setLoadingEmployees(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [businessId]);

  // fetch inactive once so the top "Total Employees" stat has both counts
useEffect(() => {
  loadInactive(); // respects inactiveLoaded, so it won't refetch later
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  // inactive (lazy)
  const loadInactive = async () => {
    if (inactiveLoaded) return;
    setLoadingInactive(true);
    try {
      const { data } = await api.get(`/employees/${businessId}/inactive`);
      setInactive(Array.isArray(data) ? data : []);
      setInactiveLoaded(true);
    } catch (e) {
      console.error("Failed to fetch inactive employees:", e);
      alert("Failed to fetch inactive employees.");
    } finally {
      setLoadingInactive(false);
    }
  };

  // departments (lazy)
  const loadDepartments = async () => {
    if (deptLoaded) return;
    setLoadingDept(true);
    try {
      const { data } = await api.get(`/business/${businessId}/departments`);
      setDepartments(Array.isArray(data) ? data : []);
      setDeptLoaded(true);
    } catch (e) {
      console.error("Error fetching departments:", e);
      setDeptError("Failed to load departments.");
    } finally {
      setLoadingDept(false);
    }
  };

  /** -------- Actions -------- */
  const handleDisable = async (employeeId) => {
    if (!window.confirm("Are you sure you want to disable this employee?")) return;
    try {
      const res = await api.put(`/employees/${employeeId}/disable`);
      const { message } = res.data || {};
      setEmployees((prev) => prev.filter((e) => e.id !== employeeId));
      if (message) alert(message);
    } catch (error) {
      console.error("Error disabling employee:", error);
      alert("Failed to disable employee.");
    }
  };

  const handleEnable = async (employeeId) => {
    try {
      await api.put(`/employees/${employeeId}/enable`);
      setInactive((prev) => prev.filter((e) => e.id !== employeeId));
    } catch (e) {
      console.error("Failed to enable employee:", e);
      alert("Error enabling employee.");
    }
  };


  // Preload departments so we can map department_id -> name in the table
useEffect(() => {
  loadDepartments(); // safe: loadDepartments bails out if already loaded
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [businessId]);


// Map department id -> { name, short }
const deptMap = useMemo(() => {
  const m = {};
  for (const d of departments) {
    m[d.id] = {
      name: d.department_name || d.name || "",
      short: d.department_short || "",
    };
  }
  return m;
}, [departments]);


useEffect(() => {
  const onDocClick = (e) => {
    if (!(e.target.closest(".emp-kebab-btn") || e.target.closest(".emp-menu"))) {
      setOpenMenuId(null);
    }
  };
  const onKey = (e) => { if (e.key === "Escape") setOpenMenuId(null); };
  document.addEventListener("click", onDocClick);
  document.addEventListener("keydown", onKey);
  return () => {
    document.removeEventListener("click", onDocClick);
    document.removeEventListener("keydown", onKey);
  };
}, []);

const renderDept = (emp) => {
  const d = emp?.department_id ? deptMap[emp.department_id] : null;
  if (d?.name) return d.short ? `${d.name} (${d.short})` : d.name;
  // fallbacks if deptMap isn't loaded yet or no id present
  return emp.department || emp.department_name || "—";
};


  const handleDeductions = async (employeeId) => {
  try {
    const businessId = selectedBusiness?.id;

    if (!businessId) {
      alert("No business selected.");
      return;
    }

    const { data } = await api.get(
      `/businesses/${businessId}/employees/${employeeId}/deductions`
    );

    if (data) {
      navigate("/dashboard/edit-deductions", {
        state: { employeeId },
      });
    } else {
      navigate("/dashboard/add-deductions", {
        state: { employeeId },
      });
    }
  } catch (error) {
    console.error("Error checking deductions:", error);
    alert("Failed to determine deductions page.");
  }
};

  // put this near your other small helpers
const prettyWorkLoc = (loc) => {
  const v = (loc ?? "").toString().trim().toLowerCase();
  if (!v || v === "bus") return "On site";
  if (v === "home") return "Works from home";
  return loc; // anything else: show as-is
};


  const handleAddDepartment = async (e) => {
    e.preventDefault();
    if (!newDepartmentName || !newDepartmentShort) {
      setDeptError("Please enter department details.");
      return;
    }
    try {
      const response = await api.post(`/business/${businessId}/departments`, {
        department_name: newDepartmentName,
        department_short: newDepartmentShort,
      });
      setDepartments((prev) => [
        ...prev,
        { id: response?.data?.id, department_name: newDepartmentName, department_short: newDepartmentShort },
      ]);
      setNewDepartmentName("");
      setNewDepartmentShort("");
      setDeptError(null);
    } catch (error) {
      console.error("Error adding department:", error);
      setDeptError("Failed to add department.");
    }
  };

  /** -------- Derivations -------- */
  const stats = useMemo(() => {
  const activeCount = employees.filter(e => e?.is_active ?? true).length;
  const inactiveCount = inactive.length;
  const total = activeCount + inactiveCount;

  // 60-day "new hire" window
  const HIRE_WINDOW_DAYS = 60;
  const cutoffTime = Date.now() - HIRE_WINDOW_DAYS * 24 * 60 * 60 * 1000;

  const getHireDate = (e) => e?.hire_date || e?.join_date || e?.start_date || null;

  // Count new hires among active employees
  const newHires = employees.filter(e => {
    const d = getHireDate(e);
    const t = d ? new Date(d).getTime() : NaN;
    return Number.isFinite(t) && t >= cutoffTime;
  }).length;

  const onLeave = employees.filter(e => (e?.status || "").toLowerCase() === "leave").length;

  return { total, active: activeCount, onLeave, newHires };
}, [employees, inactive]);



  const filteredEmployees = useMemo(() => {
    if (!q.trim()) return employees;
    const k = q.toLowerCase();
    return employees.filter((e) =>
      [e?.first_name, e?.last_name, e?.department, e?.department_name, e?.employee_id, e?.e_city, e?.location]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(k))
    );
  }, [employees, q]);

  const toPrettyDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    return isNaN(dt) ? "-" : dt.toLocaleDateString();
  };

  

  /** -------- UI -------- */
  return (
    <div className="pw-wrap">
      <TabSvgDefs />

      {/* top bar */}
      <div className="pw-topbar">
        
        <div className="pw-right-cluster">
          
          
          
          
        </div>
      </div>

      <h1 className="pw-page-title">Employees</h1>

      {/* stats */}
      <div className="pw-stats-grid">
        <StatCard icon={<span className="pw-icn">👥</span>} value={stats.total} label={{ title: "Total Employees", sub: "Overall workforce" }} />
        <StatCard icon={<span className="pw-icn">🏃</span>} value={stats.active} label={{ title: "Active Employees", sub: "Currently working" }} />
        <StatCard icon={<span className="pw-icn">🌀</span>} value={stats.onLeave} label={{ title: "On Leave", sub: "Approved leave today" }} />
        <StatCard icon={<span className="pw-icn">🆕</span>} value={stats.newHires} label={{ title: "New Hires", sub: "Joined withtin 60 days" }} />
      </div>


       <button className="pw-primary add-emp-btn" onClick={() => navigate(`/dashboard/business/add-employee`)}>
            + Add Employee
          </button>


      {/* ===== SVG Tabs (same look/feel as ViewPayroll) ===== */}
      <SvgTabs
        tabs={TAB_LABELS}
        activeIndex={activeTabIndex}
        onChange={(i) => {
          setActiveByIndex(i);
          const key = TAB_KEYS[i];
          if (key === "status") loadInactive();
          if (key === "department") loadDepartments();
        }}
      />

  

      {/* ===== Panels under tabs ===== */}
      <div className="svg-panels">
        {/* --- Employee Details --- */}
        <div id="panel-0" className={`pw-card tab-panel ${activeTab === "details" ? "is-active" : ""}`}>
  <div className="pw-table-shell">
    <div className="pw-table-head">
      <table className="pw-table pw-table--fixed">
        <thead>
          <tr>
            <th className="t-left">Name</th>
            <th>Department</th>
            <th className="hide-header">Join Date</th>
            <th className="hide-header">Location</th>
            <th className="t-right">Actions</th>
          </tr>
        </thead>
      </table>
    </div>

    <div className="pw-table-body-scroll">
      <table className="pw-table pw-table--body">
        <tbody>
          {loadingEmployees ? (
            <tr>
              <td colSpan="5" className="pw-empty">Loading…</td>
            </tr>
          ) : filteredEmployees.length ? (
            filteredEmployees.map((e) => (
              <tr className="employee-row" key={e.id}>
                <td className="t-left">
                  <div className="pw-profile">
                    <EmployeeAvatar first={e.first_name} last={e.last_name} />
                    <div className="pw-name-stack">
                      <div className="pw-name">{`${e.first_name || ""} ${e.last_name || ""}`.trim() || "—"}</div>
                      <div className="pw-sub">{e?.title || e?.role || ""}</div>
                    </div>
                  </div>
                </td>

                <td>{renderDept(e)}</td>
                <td className="hide-header">{toPrettyDate(e.hire_date || e.start_date)}</td>
                <td className="hide-header">{prettyWorkLoc(e.work_loc)}</td>

                <td className="t-right emp-actions-cell">
                  <button
                    type="button"
                    className="emp-kebab-btn"
                    aria-haspopup="menu"
                    aria-expanded={openMenuId === e.id}
                    aria-controls={`emp-menu-${e.id}`}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      const nextOpen = openMenuId === e.id ? null : e.id;
                      setOpenMenuId(nextOpen);
                      setMenuAnchorEl(nextOpen ? ev.currentTarget : null);
                    }}
                  >
                    <FaEllipsisV />
                  </button>

                  <EmpMenuPortal
                    anchorEl={menuAnchorEl}
                    open={openMenuId === e.id}
                    onClose={() => { setOpenMenuId(null); setMenuAnchorEl(null); }}
                  >
                    <li>
                      <button
                        className="emp-menu-item"
                        role="menuitem"
                        onClick={() => { setOpenMenuId(null); setMenuAnchorEl(null); handleDeductions(e.id); }}
                      >
                        Deductions
                      </button>
                    </li>
                    <li>
                      <button
                        className="emp-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setOpenMenuId(null); setMenuAnchorEl(null);
                          navigate("/dashboard/edit-employee", {state: { employeeId: e.id },});
                        }}
                      >
                        Edit
                      </button>
                    </li>
                    <li>
                      <button
                        className="emp-menu-item danger"
                        role="menuitem"
                        onClick={() => { setOpenMenuId(null); setMenuAnchorEl(null); handleDisable(e.id); }}
                      >
                        Disable
                      </button>
                    </li>
                  </EmpMenuPortal>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="pw-empty">No employees found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
</div>

        {/* --- Employee Status (Inactive) --- */}
        <div id="panel-1" className={`pw-card tab-panel ${activeTab === "status" ? "is-active" : ""}`}>
  <div className="pw-table-shell">
    <div className="pw-table-head">
      <table className="pw-table pw-table--fixed">
        <thead>
          <tr>
            <th>First Name</th>
            <th>Last Name</th>
            <th className="hide-header">City</th>
            <th>Disabled On</th>
            <th className="t-right">Actions</th>
          </tr>
        </thead>
      </table>
    </div>

    <div className="pw-table-body-scroll">
      <table className="pw-table pw-table--body">
        <tbody>
          {loadingInactive ? (
            <tr>
              <td colSpan="5" className="pw-empty">Loading…</td>
            </tr>
          ) : inactive.length === 0 ? (
            <tr>
              <td colSpan="5" className="pw-empty">No inactive employees found.</td>
            </tr>
          ) : (
            inactive.map((emp) => (
              <tr key={emp.id}>
                <td>{emp.first_name}</td>
                <td>{emp.last_name}</td>
                <td className="hide-header">{emp.e_city || "—"}</td>
                <td>{toPrettyDate(emp.disabled_at)}</td>
                <td className="t-right">
                  <button className="pw-primary" onClick={() => handleEnable(emp.id)}>
                    Enable
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
</div>
        

        {/* --- Add Department --- */}
        <div id="panel-2" className={`pw-card tab-panel ${activeTab === "department" ? "is-active" : ""}`}>
          <div className="departments-container" style={{ padding: 16 }}>
            <h3 style={{ margin: "8px 0 16px" }}>Departments</h3>

            <div className="add-department-box" style={{ marginBottom: 16 }}>
              {deptError && <p className="error">{deptError}</p>}
              <form className="department-form" onSubmit={handleAddDepartment} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  type="text"
                  placeholder="Department Name"
                  value={newDepartmentName}
                  onChange={(e) => setNewDepartmentName(e.target.value)}
                  required
                />
                <input
                  type="text"
                  placeholder="Short Form (e.g., HR, IT)"
                  value={newDepartmentShort}
                  onChange={(e) => setNewDepartmentShort(e.target.value)}
                  required
                />
                <button type="submit" className="pw-primary">
                  Add Department
                </button>
              </form>
            </div>

            <h4>Existing Departments</h4>
            {loadingDept ? (
              <p className="pw-empty">Loading…</p>
            ) : departments.length === 0 ? (
              <p className="pw-empty">No departments found.</p>
            ) : (
              <ul style={{ padding: "6px 18px" }}>
                {departments.map((d) => (
                  <li key={d.id} style={{ lineHeight: "28px" }}>
                    {d.department_name} ({d.department_short})
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeesPage;




// import React, { useEffect, useMemo, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { FaEdit, FaUserTimes, FaEllipsisV } from "react-icons/fa";
// import api from "../services/api";
// import "./EmployeesPage.css";

// /* Small presentational helpers (unchanged) */
// const Dot = ({ color = "#2ecc71" }) => <span className="pw-dot" style={{ backgroundColor: color }} />;
// const EmployeeAvatar = ({ first = "", last = "" }) => {
//   const initials = `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();
//   return <div className="pw-avatar">{initials || "?"}</div>;
// };
// const StatCard = ({ icon, label, value }) => (
//   <div className="pw-stat">
//     <div className="pw-stat-icon">{icon}</div>
//     <div className="pw-stat-right">
//       <div className="pw-stat-value">{value}</div>
//       <div className="pw-stat-label">
//         {label.title}
//         <div className="pw-stat-sub">{label.sub}</div>
//       </div>
//     </div>
//   </div>
// );

// const EmployeesPage = () => {
//   const { id: businessId } = useParams();
//   const navigate = useNavigate();

//   /* -------- page state -------- */
//   const [activeTab, setActiveTab] = useState("details"); // "details" | "status" | "department"

//   // details tab
//   const [employees, setEmployees] = useState([]);
//   const [loadingEmployees, setLoadingEmployees] = useState(true);
//   const [q, setQ] = useState("");

//   // status tab (inactive employees)
//   const [inactive, setInactive] = useState([]);
//   const [loadingInactive, setLoadingInactive] = useState(false);
//   const [inactiveLoaded, setInactiveLoaded] = useState(false);

//   // department tab
//   const [departments, setDepartments] = useState([]);
//   const [deptLoaded, setDeptLoaded] = useState(false);
//   const [loadingDept, setLoadingDept] = useState(false);
//   const [newDepartmentName, setNewDepartmentName] = useState("");
//   const [newDepartmentShort, setNewDepartmentShort] = useState("");
//   const [deptError, setDeptError] = useState(null);

//   /* -------- fetchers -------- */
//   // employees
//   useEffect(() => {
//     let alive = true;
//     (async () => {
//       try {
//         const { data } = await api.get(`/businesses/${businessId}/employees`);
//         if (!alive) return;
//         setEmployees(Array.isArray(data) ? data : []);
//       } catch (e) {
//         console.error("Error fetching employees:", e);
//         alert("Failed to load employees.");
//       } finally {
//         alive && setLoadingEmployees(false);
//       }
//     })();
//     return () => { alive = false; };
//   }, [businessId]);

//   // inactive (lazy: only when tab opens first time)
//   const loadInactive = async () => {
//     if (inactiveLoaded) return;
//     setLoadingInactive(true);
//     try {
//       const { data } = await api.get(`/employees/${businessId}/inactive`);
//       setInactive(Array.isArray(data) ? data : []);
//       setInactiveLoaded(true);
//     } catch (e) {
//       console.error("Failed to fetch inactive employees:", e);
//       alert("Failed to fetch inactive employees.");
//     } finally {
//       setLoadingInactive(false);
//     }
//   };

//   // departments (lazy)
//   const loadDepartments = async () => {
//     if (deptLoaded) return;
//     setLoadingDept(true);
//     try {
//       const { data } = await api.get(`/business/${businessId}/departments`);
//       setDepartments(Array.isArray(data) ? data : []);
//       setDeptLoaded(true);
//     } catch (e) {
//       console.error("Error fetching departments:", e);
//       setDeptError("Failed to load departments.");
//     } finally {
//       setLoadingDept(false);
//     }
//   };

//   /* -------- actions -------- */
//   const handleDisable = async (employeeId) => {
//     if (!window.confirm("Are you sure you want to disable this employee?")) return;
//     try {
//       const res = await api.put(`/employees/${employeeId}/disable`);
//       const { message } = res.data || {};
//       setEmployees(prev => prev.filter(e => e.id !== employeeId));
//       if (message) alert(message);
//       // also remove from active filtered lists if any
//     } catch (error) {
//       console.error("Error disabling employee:", error);
//       alert("Failed to disable employee.");
//     }
//   };

//   const handleEnable = async (employeeId) => {
//     try {
//       await api.put(`/employees/${employeeId}/enable`);
//       setInactive(prev => prev.filter(e => e.id !== employeeId));
//     } catch (e) {
//       console.error("Failed to enable employee:", e);
//       alert("Error enabling employee.");
//     }
//   };

//   const handleDeductions = async (employeeId) => {
//     try {
//       const { data } = await api.get(`/businesses/${businessId}/employees/${employeeId}/deductions`);
//       if (data) {
//         navigate(`/dashboard/business/${businessId}/edit-deductions/${employeeId}`);
//       } else {
//         navigate(`/dashboard/business/${businessId}/add-deductions/${employeeId}`);
//       }
//     } catch (error) {
//       console.error("Error checking deductions:", error);
//       alert("Failed to determine deductions page.");
//     }
//   };

//   const handleAddDepartment = async (e) => {
//     e.preventDefault();
//     if (!newDepartmentName || !newDepartmentShort) {
//       setDeptError("Please enter department details.");
//       return;
//     }
//     try {
//       const response = await api.post(`/business/${businessId}/departments`, {
//         department_name: newDepartmentName,
//         department_short: newDepartmentShort,
//       });
//       setDepartments(prev => [
//         ...prev,
//         { id: response?.data?.id, department_name: newDepartmentName, department_short: newDepartmentShort },
//       ]);
//       setNewDepartmentName("");
//       setNewDepartmentShort("");
//       setDeptError(null);
//     } catch (error) {
//       console.error("Error adding department:", error);
//       setDeptError("Failed to add department.");
//     }
//   };

//   /* -------- derivations -------- */
//   const stats = useMemo(() => {
//     const total = employees.length;
//     const active = employees.filter(e => e?.is_active ?? true).length;
//     const onLeave = employees.filter(e => (e?.status || "").toLowerCase() === "leave").length;
//     const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
//     const newHires = employees.filter(e => {
//       const d = e?.join_date || e?.start_date;
//       return d ? new Date(d) >= cutoff : false;
//     }).length;
//     return { total, active, onLeave, newHires };
//   }, [employees]);

//   const filteredEmployees = useMemo(() => {
//     if (!q.trim()) return employees;
//     const k = q.toLowerCase();
//     return employees.filter(e =>
//       [e?.first_name, e?.last_name, e?.department, e?.department_name, e?.employee_id, e?.e_city, e?.location]
//         .filter(Boolean)
//         .some(v => String(v).toLowerCase().includes(k))
//     );
//   }, [employees, q]);

//   const toPrettyDate = (d) => {
//     if (!d) return "-";
//     const dt = new Date(d);
//     return isNaN(dt) ? "-" : dt.toLocaleDateString();
//   };
//   const payrollStatusCell = (row) => {
//     const s = (row?.payroll_status || "Complete").toLowerCase();
//     if (s.includes("pending")) return <><Dot color="#e2b203" /><span>Pending</span></>;
//     if (s.includes("failed")) return <><Dot color="#e5484d" /><span>Failed</span></>;
//     return <><Dot color="#2ecc71" /><span>Complete</span></>;
//   };

//   /* -------- UI -------- */
//   return (
//     <div className="pw-wrap">
//       {/* top bar */}
//       <div className="pw-topbar">
//         <div className="pw-search">
//           <span className="pw-search-icon">🔍</span>
//           <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" aria-label="Search employees" />
//         </div>
//         <div className="pw-right-cluster">
//           <div className="pw-flag">🇬🇧</div>
//           <div className="pw-business-pill"><span>Business Name</span><span className="pw-caret">▾</span></div>
//           <div className="pw-user-pill"><div className="pw-user-avatar">H</div><span>Harsh</span><span className="pw-caret">▾</span></div>
//           <button className="pw-primary" onClick={() => navigate(`/dashboard/business/${businessId}/add-employee`)}>+ Add Employee</button>
//         </div>
//       </div>

//       <h1 className="pw-page-title">Employee</h1>

//       {/* stats */}
//       <div className="pw-stats-grid">
//         <StatCard icon={<span className="pw-icn">👥</span>} value={stats.total} label={{ title: "Total Employees", sub: "Overall workforce" }} />
//         <StatCard icon={<span className="pw-icn">🏃</span>} value={stats.active} label={{ title: "Active Employees", sub: "Currently working" }} />
//         <StatCard icon={<span className="pw-icn">🌀</span>} value={stats.onLeave} label={{ title: "On Leave", sub: "Approved leave today" }} />
//         <StatCard icon={<span className="pw-icn">🆕</span>} value={stats.newHires} label={{ title: "New Hires", sub: "Joined recently" }} />
//       </div>

//       {/* tabs */}
//       <div className="pw-tabs">
//         <button className={`pw-tab ${activeTab === "details" ? "pw-tab-active" : ""}`} onClick={() => setActiveTab("details")}>
//           Employee Details
//         </button>
//         <button
//           className={`pw-tab ${activeTab === "status" ? "pw-tab-active" : ""}`}
//           onClick={() => { setActiveTab("status"); loadInactive(); }}
//         >
//           Employee Status
//         </button>
//         <button
//           className={`pw-tab ${activeTab === "department" ? "pw-tab-active" : ""}`}
//           onClick={() => { setActiveTab("department"); loadDepartments(); }}
//         >
//           Add Department
//         </button>
//       </div>

//       {/* panels */}
//       {/* --- Employee Details --- */}
//       <div className={`pw-card tab-panel ${activeTab === "details" ? "is-active" : ""}`}>
//         <table className="pw-table">
//           <thead>
//             <tr>
//               <th className="t-left">Profile</th>
//               <th>Employee ID</th>
//               <th>Department</th>
//               <th>Join Date</th>
//               <th>Location</th>
//               <th>Payroll Status</th>
//               <th className="t-right">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {loadingEmployees ? (
//               <tr><td colSpan="7" className="pw-empty">Loading…</td></tr>
//             ) : filteredEmployees.length ? (
//               filteredEmployees.map((e) => (
//                 <tr key={e.id}>
//                   <td className="t-left">
//                     <div className="pw-profile">
//                       <EmployeeAvatar first={e.first_name} last={e.last_name} />
//                       <div className="pw-name-stack">
//                         <div className="pw-name">{`${e.first_name || ""} ${e.last_name || ""}`.trim() || "—"}</div>
//                         <div className="pw-sub">{e?.title || e?.role || ""}</div>
//                       </div>
//                     </div>
//                   </td>
//                   <td>{e.employee_id ? `#${e.employee_id}` : "—"}</td>
//                   <td>{e.department || e.department_name || "—"}</td>
//                   <td>{toPrettyDate(e.join_date || e.start_date)}</td>
//                   <td>{e.location || e.e_city || "On site"}</td>
//                   <td className="pw-status">{payrollStatusCell(e)}</td>
//                   <td className="t-right">
//                     <div className="pw-actions">
//                       <button className="pw-chip" onClick={() => handleDeductions(e.id)}>Deductions</button>
//                       <FaEdit className="pw-icon-edit" title="Edit" onClick={() => navigate(`/dashboard/business/${businessId}/edit-employee/${e.id}`)} />
//                       <FaUserTimes className="pw-icon-disable" title="Disable" onClick={() => handleDisable(e.id)} />
//                       <FaEllipsisV className="pw-icon-ellipsis" title="More" />
//                     </div>
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr><td colSpan="7" className="pw-empty">No employees found.</td></tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* --- Employee Status (Inactive) --- */}
//       <div className={`pw-card tab-panel ${activeTab === "status" ? "is-active" : ""}`}>
//         <table className="pw-table">
//           <thead>
//             <tr>
//               <th>First Name</th>
//               <th>Last Name</th>
//               <th>City</th>
//               <th className="t-right">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {loadingInactive ? (
//               <tr><td colSpan="4" className="pw-empty">Loading…</td></tr>
//             ) : inactive.length === 0 ? (
//               <tr><td colSpan="4" className="pw-empty">No inactive employees found.</td></tr>
//             ) : (
//               inactive.map(emp => (
//                 <tr key={emp.id}>
//                   <td>{emp.first_name}</td>
//                   <td>{emp.last_name}</td>
//                   <td>{emp.e_city || "—"}</td>
//                   <td className="t-right">
//                     <button className="pw-primary" onClick={() => handleEnable(emp.id)}>Enable</button>
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* --- Add Department --- */}
//       <div className={`pw-card tab-panel ${activeTab === "department" ? "is-active" : ""}`}>
//         <div className="departments-container" style={{ padding: 16 }}>
//           <h3 style={{ margin: "8px 0 16px" }}>Departments</h3>

//           <div className="add-department-box" style={{ marginBottom: 16 }}>
//             {deptError && <p className="error">{deptError}</p>}
//             <form className="department-form" onSubmit={handleAddDepartment} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
//               <input
//                 type="text"
//                 placeholder="Department Name"
//                 value={newDepartmentName}
//                 onChange={(e) => setNewDepartmentName(e.target.value)}
//                 required
//               />
//               <input
//                 type="text"
//                 placeholder="Short Form (e.g., HR, IT)"
//                 value={newDepartmentShort}
//                 onChange={(e) => setNewDepartmentShort(e.target.value)}
//                 required
//               />
//               <button type="submit" className="pw-primary">Add Department</button>
//             </form>
//           </div>

//           <h4>Existing Departments</h4>
//           {loadingDept ? (
//             <p className="pw-empty">Loading…</p>
//           ) : departments.length === 0 ? (
//             <p className="pw-empty">No departments found.</p>
//           ) : (
//             <ul style={{ padding: "6px 18px" }}>
//               {departments.map((d) => (
//                 <li key={d.id} style={{ lineHeight: "28px" }}>
//                   {d.department_name} ({d.department_short})
//                 </li>
//               ))}
//             </ul>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default EmployeesPage;




// import React, { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { FaEdit, FaUserTimes } from "react-icons/fa"; // Import icons
// import api from "../services/api";
// import "./EmployeesPage.css"; // Import the CSS file for styling

// const EmployeesPage = () => {
//     const { id: businessId } = useParams();
//     const navigate = useNavigate();

//     const [employees, setEmployees] = useState([]);

//     // Fetch employees for the business
//     useEffect(() => {
//         const fetchEmployees = async () => {
//             try {
//                 const { data } = await api.get(`/businesses/${businessId}/employees`);
//                 setEmployees(data);
//             } catch (error) {
//                 console.error("Error fetching employees:", error);
//                 alert("Failed to load employees.");
//             }
//         };
//         fetchEmployees();
//     }, [businessId]);

//     // Disable an employee
//     const handleDisable = async (employeeId) => {
//         if (window.confirm("Are you sure you want to disable this employee?")) {
//             try {
//                 const response = await api.put(`/employees/${employeeId}/disable`);
//                 const { disabled, disabled_at, message } = response.data;

//                 console.log(`Employee disabled:`, { disabled, disabled_at });

//                 setEmployees(employees.filter((employee) => employee.id !== employeeId));
//                 alert(message);
//             } catch (error) {
//                 console.error("Error disabling employee:", error);
//                 alert("Failed to disable employee.");
//             }
//         }
//     };

//     const handleDeductions = async (employeeId) => {
//         try {
//             const { data } = await api.get(`/businesses/${businessId}/employees/${employeeId}/deductions`);
//             if (data) {
//                 navigate(`/dashboard/business/${businessId}/edit-deductions/${employeeId}`);
//             } else {
//                 navigate(`/dashboard/business/${businessId}/add-deductions/${employeeId}`);
//             }
//         } catch (error) {
//             console.error("Error checking deductions:", error);
//             alert("Failed to determine deductions page.");
//         }
//     };

//     return (
//         <div className="employees-page-container">
//             <h3 className="page-title-employees">Employees</h3>
//             <div className="emp-buttons">
//             <button
//                 className="add-employee-button"
//                 onClick={() => navigate(`/dashboard/business/${businessId}/add-employee`)}
//             >
//                 Add Employee
//             </button>
//                                     <button
//                                         className="inactive-button"
//                                         onClick={() =>
//                                             navigate(`/dashboard/business/${businessId}/inactive`)
//                                         }
//                                     >
//                                         Inactive Employees
//                                     </button>
//                                     <button
//                                         className="dept-button"
//                                         onClick={() =>
//                                             navigate(`/dashboard/business/${businessId}/departments`)
//                                         }
//                                     >
//                                         Departments
//                                     </button>
//                                     </div>
//             <table className="employees-table">
//                 <thead>
//                     <tr>
//                         <th className="name-column">First Name</th>
//                         <th className="last-column">Last Name</th>
//                         <th></th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {employees.length > 0 ? (
//                         employees.map((employee) => (
//                             <tr key={employee.id}>
//                                 <td className="font-name">{employee.first_name}</td>
//                                 <td className="font-name">{employee.last_name}</td>
//                                 <td>
//                                 <button
//                                         className="deductions-button"
//                                         onClick={() => handleDeductions(employee.id)}
//                                     >
//                                         Deductions
//                                     </button>
//                                 <FaEdit 
//                                         className="edit-action " 
//                                         onClick={() => navigate(`/dashboard/business/${businessId}/edit-employee/${employee.id}`)} 
//                                         title="Edit Employee"
//                                     />
//                                     {/* <button
//                                         className="edit-button"
//                                         onClick={() =>
//                                             navigate(`/dashboard/business/${businessId}/edit-employee/${employee.id}`)
//                                         }
//                                     >
//                                         Edit
//                                     </button> */}
//                                     <FaUserTimes 
//                                         className="action-icon disable-icon disable-emp" 
//                                         onClick={() => handleDisable(employee.id)} 
//                                         title="Disable Employee"
//                                     />
                                    
                                    
//                                     {/* <button
//                                         className="disable-button"
//                                         onClick={() => handleDisable(employee.id)}
//                                     >
//                                         Disable
//                                     </button> */}
//                                 </td>
//                             </tr>
//                         ))
//                     ) : (
//                         <tr>
//                             <td colSpan="3" className="no-employees">
//                                 No employees found.
//                             </td>
//                         </tr>
//                     )}
//                 </tbody>
//             </table>
//         </div>
//     );
// };

// export default EmployeesPage;



// import React, { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import api from "../services/api";
// import "./EmployeesPage.css";

// const EmployeesPage = () => {
//     const { id: businessId } = useParams();
//     const navigate = useNavigate();

//     const [employees, setEmployees] = useState([]);

//     // Fetch employees for the business
//     useEffect(() => {
//         const fetchEmployees = async () => {
//             try {
//                 const { data } = await api.get(`/businesses/${businessId}/employees`);
//                 setEmployees(data);
//             } catch (error) {
//                 console.error("Error fetching employees:", error);
//                 alert("Failed to load employees.");
//             }
//         };

//         fetchEmployees();
//     }, [businessId]);

//     // Disable an employee
//     const handleDisable = async (employeeId) => {
//         if (window.confirm("Are you sure you want to disable this employee?")) {
//             try {
//                 const response = await api.put(`/employees/${employeeId}/disable`);
//                 const { disabled, disabled_at, message } = response.data;

//                 // Log the backend response for debugging
//                 console.log(`Employee disabled:`, { disabled, disabled_at });

//                 // Update UI
//                 setEmployees(employees.filter((employee) => employee.id !== employeeId));

//                 alert(message);
//             } catch (error) {
//                 console.error("Error disabling employee:", error);
//                 alert("Failed to disable employee.");
//             }
//         }
//     };

//     const handleDeductions = async (employeeId) => {
//         try {
//             const { data } = await api.get(`/businesses/${businessId}/employees/${employeeId}/deductions`);
    
//             // Navigate based on the presence of data
//             if (data) {
//                 navigate(`/dashboard/business/${businessId}/edit-deductions/${employeeId}`);
//             } else {
//                 navigate(`/dashboard/business/${businessId}/add-deductions/${employeeId}`);
//             }
//         } catch (error) {
//             console.error("Error checking deductions:", error);
//             alert("Failed to determine deductions page.");
//         }
//     };
    

//     return (
//         <div>
//             <h2>Employees</h2>
//             <button style={{width:"10%", position:"absolute", right:"5%", top:"25%"}} onClick={() => navigate(`/dashboard/business/${businessId}/add-employee`)}>Add Employee</button>
//             <table width="800px" cellPadding="8" cellSpacing="0">
//                 <thead>
//                     <tr>
//                         <th>Employee Name</th>
                        
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {employees.length > 0 ? (
//                         employees.map((employee) => (
//                             <tr key={employee.id}>
//                                 <td>{employee.first_name}</td>
//                                 <td>{employee.last_name}</td>
//                                 <td>
//                                     <button style={{width:"25%"}}
//                                         onClick={() =>
//                                             navigate(`/dashboard/business/${businessId}/edit-employee/${employee.id}`)
//                                         }
//                                     >
//                                         Edit
//                                     </button>

//                                     <button onClick={() => handleDeductions(employee.id)}>Deductions</button>
//                                     <button style={{width:"25%"}} onClick={() => handleDisable(employee.id)}>Disable</button>
//                                 </td>
//                             </tr>
//                         ))
//                     ) : (
//                         <tr>
//                             <td colSpan="3">No employees found.</td>
//                         </tr>
//                     )}
//                 </tbody>
//             </table>
//         </div>
//     );
// };

// export default EmployeesPage;
